/**
 * SedeSelector - Selettore sede per admin
 *
 * Mostra la sede corrente e permette di cambiarla (solo per admin).
 * Per operatori con sede fissa, mostra solo un badge.
 * Usa Portal per evitare problemi di z-index con l'header.
 */

import { useState, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, ChevronDown, Check } from 'lucide-react';
import SedeContext, { SEDI, type SedeType } from '../contexts/SedeContext';

interface SedeSelectorProps {
  compact?: boolean; // Versione compatta per mobile
  className?: string;
}

export default function SedeSelector({ compact = false, className = '' }: SedeSelectorProps) {
  const context = useContext(SedeContext);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  // Calcola posizione dropdown quando si apre
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8, // 8px di margine
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  // Chiudi dropdown al click fuori
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Chiudi su scroll
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => setIsOpen(false);
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [isOpen]);

  // Se context non disponibile, non renderizzare nulla
  if (!context) {
    return null;
  }

  const { sedeCorrente, sedeInfo, puoCambiareSede, cambiareSede, sediDisponibili, isLoading } = context;

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/50 ${className}`}>
        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Se non può cambiare sede, mostra solo badge
  if (!puoCambiareSede) {
    return (
      <div
        className={`flex items-center gap-2 px-4 rounded-lg transition-all duration-300 hover:scale-[1.15] hover:shadow-2xl hover:z-50 backdrop-blur-sm h-[48px] ${className}`}
        style={{
          background: sedeCorrente === 'misterbianco'
            ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)'
            : 'linear-gradient(135deg, #ea580c 0%, #c2410c 50%, #9a3412 100%)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}
      >
        <MapPin className="w-4 h-4 text-white" />
        <span className="font-medium text-white">
          {compact ? sedeInfo.nomeBreve : sedeInfo.nome}
        </span>
      </div>
    );
  }

  // Dropdown renderizzato via Portal
  const dropdown = isOpen ? createPortal(
    <div
      ref={dropdownRef}
      className="fixed w-48 bg-slate-800 border border-gray-600 rounded-lg shadow-2xl overflow-hidden"
      style={{
        top: dropdownPosition.top,
        right: dropdownPosition.right,
        zIndex: 99999
      }}
    >
      <div className="px-3 py-2 border-b border-gray-600">
        <p className="text-xs text-gray-400 uppercase tracking-wide">Seleziona Sede</p>
      </div>
      {sediDisponibili.map((sede) => (
        <button
          key={sede.id}
          onClick={() => {
            cambiareSede(sede.id);
            setIsOpen(false);
          }}
          className={`w-full flex items-center justify-between px-3 py-3 hover:bg-slate-700 transition-colors ${
            sede.id === sedeCorrente ? 'bg-slate-700/50' : ''
          }`}
        >
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${sede.id === 'misterbianco' ? 'bg-blue-500' : 'bg-orange-500'}`} />
            <span className="font-medium text-white">{sede.nome}</span>
          </div>
          {sede.id === sedeCorrente && (
            <Check className="w-4 h-4 text-green-400" />
          )}
        </button>
      ))}
    </div>,
    document.body
  ) : null;

  // Admin: dropdown per cambiare sede
  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 rounded-lg cursor-pointer transition-all duration-300 hover:scale-[1.15] hover:shadow-2xl hover:z-50 backdrop-blur-sm h-[48px] relative"
        style={{
          background: sedeCorrente === 'misterbianco'
            ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #1e40af 100%)'
            : 'linear-gradient(135deg, #ea580c 0%, #c2410c 50%, #9a3412 100%)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}
      >
        <MapPin className="w-4 h-4 text-white" />
        <span className="font-medium text-white">
          {compact ? sedeInfo.nomeBreve : sedeInfo.nome}
        </span>
        <ChevronDown className={`w-4 h-4 text-white transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {dropdown}
    </div>
  );
}

// Badge semplice per mostrare la sede di una risorsa
export function SedeBadge({ sede, compact = false, showNd = true }: { sede: SedeType | null | undefined; compact?: boolean; showNd?: boolean }) {
  // Se sede è null/undefined, mostra badge N/D se showNd è true
  if (!sede) {
    if (!showNd) return null;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-500/30 text-gray-400 border border-gray-500/30">
        <MapPin className="w-3 h-3" />
        N/D
      </span>
    );
  }

  const info = SEDI[sede];
  if (!info) return null;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${info.coloreBg} ${info.colore}`}>
      <MapPin className="w-3 h-3" />
      {compact ? info.nomeBreve : info.nome}
    </span>
  );
}
